import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule, FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { UsageSettingsService } from '../../features/usage-settings/usage-settings.service';
import { ContentLoaderComponent } from '../../shared/components/content-loader-component/contentLoader.component';
import { PermissionDirective } from '../../shared/components/directives/permissions.directives';

class TouchedErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return !!(control && control.invalid && (control.dirty || control.touched || (form && form.submitted)));
  }
}

interface UsageSetting {
  id: number;
  planName: string;
  feature: string;
  usageLimit: number;
  isActive: boolean;
}

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    ContentLoaderComponent,
    PermissionDirective,
  ],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss',
  providers: [
    { provide: ErrorStateMatcher, useClass: TouchedErrorStateMatcher }
  ],
})
export class SettingsComponent implements OnInit, OnDestroy {
  settings: UsageSetting[] = [];
  plans = ['Premium', 'PremiumPlus'];
  features = ['Read with AIMO', 'Story Generation', 'Audio Generation'];
  filteredFeatures: string[] = [];
  loading = false;

  createForm!: FormGroup;
  creatingInProgress = false;

  editingSettingId: number | null = null;
  editLimitValue = 0;
  updatingLimit = false;
  updatingToggle = false;

  planDisplayNames: Record<string, string> = {
    Premium: 'Premium',
    PremiumPlus: 'Premium Plus',
  };

  maxFeaturesPerPlan = 3;

  get availablePlans(): string[] {
    return this.plans.filter(p => this.getSettingsForPlan(p).length < this.maxFeaturesPerPlan);
  }

  getSettingsForPlan(planName: string): UsageSetting[] {
    return this.settings.filter(s => s.planName === planName);
  }

  getAvailableFeatures(planName: string): string[] {
    const usedFeatures = this.getSettingsForPlan(planName).map(s => s.feature);
    return this.features.filter(f => !usedFeatures.includes(f));
  }

  private subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private usageSettingsService: UsageSettingsService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.initCreateForm();
    this.fetchSetting();
    this.filteredFeatures = [...this.features];
  }

  initCreateForm() {
    this.createForm = this.fb.group({
      planName: ['', [Validators.required]],
      feature: ['', [Validators.required]],
      usageLimit: [1, [Validators.required, Validators.min(1)]],
      isActive: [true],
    });
  }

  onPlanChange() {
    this.createForm.get('feature')?.setValue('');
    const selectedPlan = this.createForm.get('planName')?.value || '';
    this.filteredFeatures = selectedPlan ? this.getAvailableFeatures(selectedPlan) : [...this.features];
  }

  fetchSetting() {
    this.loading = true;
    this.subscriptions.add(
      this.usageSettingsService.getUsageSetting().subscribe({
        next: (res: any) => {
          this.settings = res.data || [];
          this.loading = false;
        },
        error: () => {
          this.settings = [];
          this.loading = false;
        },
      })
    );
  }

  onCreateSetting() {
    if (this.createForm.invalid) return;
    this.creatingInProgress = true;
    const { planName, feature, usageLimit, isActive } = this.createForm.value;
    this.subscriptions.add(
      this.usageSettingsService.createUsageSetting({ planName, feature, usageLimit, isActive }).subscribe({
        next: (res: any) => {
          this.settings.push(res.data);
          this.createForm.reset({ planName: '', feature: '', usageLimit: 1, isActive: true });
          this.createForm.markAsPristine();
          this.createForm.markAsUntouched();
          this.creatingInProgress = false;
          this.toastr.success('Usage setting created successfully');
        },
        error: () => {
          this.creatingInProgress = false;
        },
      })
    );
  }

  toggleActive(setting: UsageSetting) {
    const newValue = !setting.isActive;
    this.updatingToggle = true;
    this.subscriptions.add(
      this.usageSettingsService.updateUsageSetting(setting.id, { isActive: newValue }).subscribe({
        next: (res: any) => {
          const index = this.settings.findIndex(s => s.id === setting.id);
          if (index !== -1) this.settings[index] = res.data;
          this.updatingToggle = false;
          this.toastr.success(`Usage setting ${newValue ? 'enabled' : 'disabled'}`);
        },
        error: () => {
          this.updatingToggle = false;
        },
      })
    );
  }

  startEditLimit(setting: UsageSetting) {
    this.editingSettingId = setting.id;
    this.editLimitValue = setting.usageLimit;
  }

  cancelEditLimit() {
    this.editingSettingId = null;
  }

  saveLimit(setting: UsageSetting) {
    if (this.editLimitValue === setting.usageLimit) {
      this.editingSettingId = null;
      return;
    }
    this.updatingLimit = true;
    this.subscriptions.add(
      this.usageSettingsService.updateUsageSetting(setting.id, { usageLimit: this.editLimitValue }).subscribe({
        next: (res: any) => {
          const index = this.settings.findIndex(s => s.id === setting.id);
          if (index !== -1) this.settings[index] = res.data;
          this.editingSettingId = null;
          this.updatingLimit = false;
          this.toastr.success('Usage setting updated successfully');
        },
        error: () => {
          this.updatingLimit = false;
        },
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
